// Formulaire.tsx
import React, { useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { countries as _countries } from "../../assets/countries";
import { useNavigate } from "react-router";
import config from "src/config/config";
import ErrorModal from "./ErrorModal/ErrorModal";
import Altcha from "./Altcha/Altcha";
import "./Form.css";
import { Stack } from "@mui/material";

const Formulaire: React.FC = () => {
    const [error, setError] = useState(null);
    const [isModaleOpen, setIsModaleOpen] = useState(false);
    const navigate = useNavigate();
    const altchaRef = useRef<HTMLInputElement>(null);

    // States
    const [inputs, setInputs] = useState({
        denomination: "",
        email: "",
        country: "",
        phoneNumber: "",
        description: "",
    });
    const [countryError, setCountryError] = useState("");
    const [descriptionCounter, setDescriptionCounter] = useState<null | number>(null);
    const [descriptionHelperMessage, setDescriptionHelperMessage] = useState<string>("0/500");
    const [emailVerificator, setEmailVerificator] = useState<null | boolean>(null);
    const [phoneNumberVerificator, setPhoneNumberVerificator] = useState<null | boolean>(null);
    const [altchaRefIsVerified, setAltchaRefIsVerified] = useState<boolean>(false);

    const verifiedCountry = getCountry();

    const onAltchaVerified = () => {
        setAltchaRefIsVerified(true);
    };

    // Fonctions
    const handleChange = (event: any) => {
        const name = event.target.name;
        const value = event.target.value;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handleCountryChange = (event: any) => {
        handleChange(event);
        !countryIsValid(event) ? setCountryError("Format de pays invalide") : setCountryError("");
    };

    const handleDescriptionChange = (event: any) => {
        handleChange(event);
        setDescriptionCounter((event.target.value as string).length);
        setDescriptionHelperMessage(updateDescriptionHelperMessage(event.target.value));
    };

    const handleEmailChange = (event: any) => {
        handleChange(event);
        const pattern = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);

        const verificator: boolean = pattern.test(event.target.value);
        setEmailVerificator(verificator ?? false);
    };

    const handlePhoneNumberChange = (event: any) => {
        handleChange(event);
        const pattern = new RegExp(/^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}?[-.\s]?\d{1,9}$/);
        const verificator: boolean = !event.target.value || pattern.test(event.target.value);
        setPhoneNumberVerificator(verificator ?? false);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();

        const data = {
            denomination: inputs.denomination,
            email: inputs.email,
            country: verifiedCountry,
            phoneNumber: inputs.phoneNumber,
            description: inputs.description,
            altcha: altchaRef.current?.value,
        };

        fetch(config.DAC_SERVER_URL + "/request-access", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        })
            .then((response) => formIsSent(response))
            .catch((error) => console.error(error));
    };

    const handleCloseModale = () => {
        setIsModaleOpen(false);
    };
    /**
     * Catch any error happening after sending data to API, and displays errors to the user.
     * If no error have been recieved, navigate to a new page.
     * @param response the data recieved from API
     */
    function formIsSent(response: any): void {
        if (response.status == 201) {
            navigate("/created");
        } else {
            setError(response);
            setIsModaleOpen(true);
            return;
        }
    }

    /**
     * Reads a dictionary of countrys and compare it to the input country.
     * If a match is found, returns its id. Returns an empty string otherwise
     * @returns the id of the country that has been found
     */
    function getCountry(): string {
        if (inputs.country == "") {
            return "";
        }

        for (const _country of _countries) {
            if (inputCountryMatchDictionnary(inputs.country, _country)) {
                return _country.id;
            }
        }

        return "";
    }

    function inputCountryMatchDictionnary(countryInput: string, countryDictionnary: any) {
        if (
            removeAccents(countryInput.toLowerCase()) === countryDictionnary.id.toLowerCase() ||
            removeAccents(countryInput.toLowerCase()) === removeAccents(countryDictionnary.label.toLowerCase())
        ) {
            return true;
        }
        return false;
    }

    /**
     * Removes every type of accentuation from a string and replace it with its plain counterpart
     * e.g. ç becomes c, à becomes a, etc...
     * @param str the string to be transformed
     * @returns the string without accentuation
     */
    function removeAccents(str: string) {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    /**
     * Determine wether an input value for the country field is a valid one or not
     * @param event the input value of the country field
     * @returns a boolean, true if valid, false otherwise
     */
    function countryIsValid(event: any): boolean {
        let countryInput = event.target.value;

        if (countryInput == "") {
            return true;
        }

        for (const country of _countries) {
            if (inputCountryMatchDictionnary(countryInput, country)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Build the message to be displayed to the user for the helper on the description field
     * @param inputValue the input value typed by the user on the description field
     * @returns the helper message to be displayed
     */
    function updateDescriptionHelperMessage(inputValue: string): string {
        const counter = inputValue.length;
        let message: string = "";
        if (!counter || !inputValue) {
            return "0/500";
        }

        message = counter.toString() + "/500";
        if (counter < 5) {
            return "Votre message doit faire plus de 5 caractères (actuellement : " + counter.toString() + ")";
        } else if (counter > 500) {
            return "Votre message doit faire moins de 500 caractères (actuellement : " + counter.toString() + ")";
        }

        return message;
    }

    return (
        <div className='form-container'>
            <h1>Formulaire de demande d'accès</h1>
            <ErrorModal isOpen={isModaleOpen} error={error} onClose={handleCloseModale} />
            <form id='form' onSubmit={handleSubmit}>
                <div className='form-row'>
                    <TextField
                        className='width-300'
                        name='denomination'
                        value={inputs.denomination || ""}
                        onChange={handleChange}
                        label='Dénomination'
                        type='text'
                        placeholder='NOM Prénom'></TextField>
                    <TextField
                        className='width-300'
                        name='email'
                        value={inputs.email || ""}
                        onChange={handleEmailChange}
                        error={emailVerificator === false}
                        helperText={emailVerificator === false ? "Veuillez saisir une adresse mail valide" : ""}
                        required
                        label='Adresse email'
                        type='email'
                        placeholder='adresse@mail.com'></TextField>
                </div>
                <div className='form-row'>
                    <div className='width-300'>
                        <TextField
                            className='width-full'
                            name='country'
                            value={inputs.country || ""}
                            onChange={handleCountryChange}
                            error={countryError !== ""}
                            helperText={countryError}
                            label='Pays (Nom ou code)'
                            type='text'
                            placeholder='Nom (ex. Espagne) ou Code (ex. FR)'></TextField>
                    </div>
                    <TextField
                        className='width-300'
                        name='phoneNumber'
                        value={inputs.phoneNumber || ""}
                        onChange={handlePhoneNumberChange}
                        error={phoneNumberVerificator === false}
                        helperText={phoneNumberVerificator === false ? "Veuillez saisir un numéro valide" : ""}
                        label='N° Téléphone'
                        type='text'
                        placeholder='+33612345678'></TextField>
                </div>
                <div className='form-row'>
                    <TextField
                        className='width-full'
                        name='description'
                        value={inputs.description || ""}
                        onChange={handleDescriptionChange}
                        required
                        multiline
                        rows={4}
                        helperText={descriptionHelperMessage}
                        error={descriptionCounter !== null && (descriptionCounter < 5 || descriptionCounter > 500)}
                        label='Détails de votre demande'
                        placeholder="Raisons pour lesquelles vous avez besoin d'un compte, nom des applications, ..."></TextField>
                </div>
                <Stack className='form-row' direction='row' justifyContent='space-between'>
                    <div className='width-300'>
                        <Altcha ref={altchaRef} onVerified={onAltchaVerified} />
                    </div>
                    <div className='button-container'>
                        <Button
                            type='submit'
                            variant='contained'
                            disabled={
                                !descriptionCounter ||
                                descriptionCounter < 5 ||
                                descriptionCounter > 500 ||
                                countryError !== "" ||
                                emailVerificator !== true ||
                                !altchaRefIsVerified
                            }>
                            Envoyer la demande
                        </Button>
                    </div>
                </Stack>
            </form>
        </div>
    );
};

export default Formulaire;
